import { useApp } from '@/contexts';

import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';

import { useSnackbar } from '@/components/feedback/SnackbarProvider';
import { useGetWorkspaceUsersQuery } from '@/features/apps/api/appsApi';
import { useUpdateDataProcessingTimeMutation } from '@/features/transformers/api/transformersApi';
import type { TransformerVersion } from '@/types';
import { logger } from '@/utils/logger';

interface VersionsDialogProps {
  open: boolean;
  onClose: () => void;
  versions: TransformerVersion[];
  currentVersion: number;
  onVersionSelect: (version: number) => void;
  isLoading?: boolean;
}

/**
 * Dialog to view and select recipe versions
 */
export const VersionsDialog: React.FC<VersionsDialogProps> = ({
  open,
  onClose,
  versions,
  currentVersion,
  onVersionSelect,
  isLoading = false,
}) => {
  const { showSnackbar } = useSnackbar();
  const { appId, tenantId } = useApp();
  const [thresholds, setThresholds] = useState<Record<string, { hours: string; minutes: string }>>(
    {}
  );
  const [updateDPT] = useUpdateDataProcessingTimeMutation();

  // Workspace users are already fetched by AppProvider, so this is a cache hit.
  // Used to resolve the numeric lastModifiedBy user ID to the user's email.
  const { data: workspaceUsers = [] } = useGetWorkspaceUsersQuery(
    { tenantId, appId },
    { skip: !appId || !tenantId }
  );

  // Matches legacy dataProtect behavior (versionsList.js) — look up by user ID
  // in the workspace permissions and return the username (email).
  const getUserEmail = (userId: string | number | undefined): string => {
    if (userId === undefined || userId === null || userId === '') return '-';
    const user = workspaceUsers.find((u) => Number(u.userId) === Number(userId));
    return user?.username || user?.email || '-';
  };

  // Initialize thresholds from versions
  React.useEffect(() => {
    if (versions.length > 0 && Object.keys(thresholds).length === 0) {
      const initialThresholds: Record<string, { hours: string; minutes: string }> = {};
      versions.forEach((v) => {
        const maxTime =
          (v as TransformerVersion & { maxDataProcTime?: string }).maxDataProcTime || '00:00:00';

        let hours = '00';
        let minutes = '00';
        if (maxTime.includes('T')) {
          const timePart = maxTime.split('T')[1] || '00:00:00';
          const parts = timePart.split(':');
          hours = (parts[0] || '00').padStart(2, '0');
          minutes = (parts[1] || '00').padStart(2, '0');
        } else {
          const parts = maxTime.split(':');
          hours = (parts[0] || '00').padStart(2, '0');
          minutes = (parts[1] || '00').padStart(2, '0');
        }
        initialThresholds[v.id] = { hours, minutes };
      });
      setThresholds(initialThresholds);
    }
  }, [versions, thresholds]);

  const handleUpdateThreshold = async (versionId: string) => {
    const threshold = thresholds[versionId];
    if (!threshold) return;

    try {
      const maxDataProcTime = `${threshold.hours}:${threshold.minutes}:00`;
      await updateDPT({ versionId, maxDataProcTime }).unwrap();
      showSnackbar('Duration threshold updated', 'success');
    } catch (error) {
      logger.error('Failed to update duration threshold:', error);
      showSnackbar('Failed to update duration threshold', 'error');
    }
  };

  const handleThresholdChange = (versionId: string, field: 'hours' | 'minutes', value: string) => {
    setThresholds((prev) => ({
      ...prev,
      [versionId]: {
        ...prev[versionId],
        [field]: value,
        ...(field === 'hours' && value === '12' ? { minutes: '00' } : {}),
      },
    }));
  };

  // Prevent closing on backdrop click or escape key
  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="xl"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ sx: { width: '95vw', maxWidth: '1600px' } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        Recipe Versions
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="body2">Loading versions...</Typography>
          </Box>
        ) : versions.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No versions found.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Version Id</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Modified</TableCell>
                  <TableCell>Modified By</TableCell>
                  <TableCell>Comment</TableCell>
                  <TableCell>Duration Threshold</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((v) => {
                  const vNum = parseInt(String(v.version), 10);
                  const isCurrent = vNum === currentVersion;
                  return (
                    <TableRow key={v.id} selected={isCurrent}>
                      <TableCell>
                        <Button
                          size="small"
                          variant={isCurrent ? 'contained' : 'outlined'}
                          onClick={() => {
                            onVersionSelect(vNum);
                            onClose();
                          }}
                        >
                          {isCurrent ? 'Current' : 'Select'}
                        </Button>
                      </TableCell>
                      <TableCell>{v.id}</TableCell>
                      <TableCell>{v.version}</TableCell>
                      <TableCell>{v.name}</TableCell>
                      <TableCell>
                        {v.createdDate ? format(new Date(v.createdDate), 'PPpp') : '-'}
                      </TableCell>
                      <TableCell>
                        {v.lastModified ? format(new Date(v.lastModified), 'PPpp') : '-'}
                      </TableCell>
                      <TableCell>{getUserEmail(v.lastModifiedBy)}</TableCell>
                      <TableCell
                        sx={{ maxWidth: 200, wordWrap: 'break-word', whiteSpace: 'normal' }}
                      >
                        {(v as TransformerVersion & { comment?: string }).comment || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Select
                          size="small"
                          value={thresholds[v.id]?.hours || '00'}
                          onChange={(e) => handleThresholdChange(v.id, 'hours', e.target.value)}
                          sx={{ minWidth: 85, mr: 1 }}
                        >
                          {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(
                            (h) => (
                              <MenuItem key={h} value={h}>
                                {h}h
                              </MenuItem>
                            )
                          )}
                        </Select>
                        <Select
                          size="small"
                          value={thresholds[v.id]?.minutes || '00'}
                          onChange={(e) => handleThresholdChange(v.id, 'minutes', e.target.value)}
                          disabled={thresholds[v.id]?.hours === '12'}
                          sx={{ minWidth: 85, mr: 1 }}
                        >
                          {Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')).map(
                            (m) => (
                              <MenuItem key={m} value={m}>
                                {m}m
                              </MenuItem>
                            )
                          )}
                        </Select>
                        <Button size="small" onClick={() => handleUpdateThreshold(v.id)}>
                          Update
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
